import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot, doc, deleteDoc, Timestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle, Trash2, Clock, MapPin, Monitor, ChevronDown, ChevronUp, User } from 'lucide-react';
import { format } from 'date-fns';

interface ErrorLog {
  id: string;
  userId?: string;
  userEmail?: string;
  location: string;
  message: string;
  stack?: string;
  context?: any;
  createdAt: string;
  userAgent: string;
}

export const ErrorLogsView: React.FC = () => {
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'error_logs'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newLogs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ErrorLog[];
      setLogs(newLogs);
      setLoading(false);
    }, (err) => {
      console.error("Failed to fetch error logs:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Delete this error log?')) {
      try {
        await deleteDoc(doc(db, 'error_logs', id));
      } catch (err) {
        console.error("Failed to delete log:", err);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">System Error Logs</h3>
        <span className="text-sm text-gray-500">{logs.length} recent issues</span>
      </div>

      {logs.length === 0 ? (
        <div className="bg-white p-12 rounded-3xl border border-dashed border-gray-200 text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No system errors detected recently.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <div 
              key={log.id} 
              className={`bg-white border rounded-2xl transition-all ${expandedLogId === log.id ? 'ring-2 ring-indigo-500 border-indigo-500 shadow-lg' : 'border-gray-100 hover:border-gray-300 shadow-sm'}`}
            >
              <div 
                className="p-4 cursor-pointer flex items-center justify-between"
                onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`p-2 rounded-lg ${log.message.includes('403') || log.message.includes('Forbidden') ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600'}`}>
                    <AlertCircle className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-bold text-gray-900 truncate">{log.message}</h4>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(new Date(log.createdAt), 'MMM d, HH:mm')}
                      </span>
                      {log.userEmail && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {log.userEmail}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={(e) => handleDelete(log.id, e)}
                    className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expandedLogId === log.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              <AnimatePresence>
                {expandedLogId === log.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 border-t border-gray-50 space-y-4 pt-4">
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Environment</label>
                          <div className="flex items-center gap-2 text-gray-600 bg-gray-50 p-2 rounded-lg">
                            <MapPin className="w-3.5 h-3.5" />
                            <span className="truncate">{log.location}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 bg-gray-50 p-2 rounded-lg">
                            <Monitor className="w-3.5 h-3.5" />
                            <span className="truncate text-xs">{log.userAgent}</span>
                          </div>
                        </div>
                        {log.context && (
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Context</label>
                            <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-xl overflow-x-auto">
                              {JSON.stringify(log.context, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                      
                      {log.stack && (
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Stack Trace</label>
                          <pre className="text-[10px] bg-red-50 text-red-700 p-4 rounded-xl overflow-x-auto font-mono whitespace-pre-wrap border border-red-100">
                            {log.stack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
